export const BLOCKS = {
  /* -------------------- CORE INPUT -------------------- */
  short_text: {
    type: "short_text",
    group: "Core",
    label: "Short Text",
    icon: "Input",
    description: "Single-line text input.",
    capabilities: { validation: true, mapping: true, logic: true, prefill: true, localization: true },
    answer: { type: "string" },
    configSchema: {
      required: ["key"],
      fields: [
        { key: "key", label: "Field key", type: "text", help: "snake_case machine key" },
        { key: "label", label: "Label", type: "text" },
        { key: "placeholder", label: "Placeholder", type: "text" },
        { key: "required", label: "Required", type: "switch" },
        { key: "validations.regex", label: "Regex", type: "regex", help: "e.g. ^[A-Za-z ]+$" },
        { key: "validations.minLength", label: "Min length", type: "number", min: 0 },
        { key: "validations.maxLength", label: "Max length", type: "number", min: 1 },
      ]
    },
    defaults: { key: "short_text", required: false, localize: true },
    renderHints: { mobile: "largeTap", desktop: "full", width: "md" },
    tier: "Free"
  },

  long_text: {
    type: "long_text",
    group: "Core",
    label: "Long Text",
    icon: "Textarea",
    description: "Multi-line input with optional Markdown.",
    capabilities: { validation: true, mapping: true, logic: true, prefill: true, localization: true },
    answer: { type: "string" },
    configSchema: {
      required: ["key"],
      fields: [
        { key: "key", label: "Field key", type: "text" },
        { key: "label", label: "Label", type: "text" },
        { key: "placeholder", label: "Placeholder", type: "textarea" },
        { key: "required", label: "Required", type: "switch" },
        { key: "validations.maxLength", label: "Max characters", type: "number", min: 1 },
      ]
    },
    defaults: { key: "notes", localize: true },
    renderHints: { mobile: "largeTap", desktop: "full", width: "lg" },
    tier: "Free"
  },

  email: {
    type: "email",
    group: "Core",
    label: "Email",
    icon: "Mail",
    description: "Validates email format.",
    capabilities: { validation: true, mapping: true, logic: true, prefill: true },
    answer: { type: "string" },
    configSchema: {
      required: ["key"],
      fields: [
        { key: "key", label: "Field key", type: "text" },
        { key: "label", label: "Label", type: "text" },
        { key: "required", label: "Required", type: "switch" },
      ]
    },
    defaults: { key: "email", required: true },
    renderHints: { mobile: "largeTap", desktop: "full" },
    tier: "Free"
  },

  phone: {
    type: "phone",
    group: "Core",
    label: "Phone",
    icon: "Phone",
    description: "International phone number with country code.",
    capabilities: { validation: true, mapping: true, logic: true, prefill: true },
    answer: { type: "string" },
    configSchema: {
      required: ["key"],
      fields: [
        { key: "key", label: "Field key", type: "text" },
        { key: "label", label: "Label", type: "text" },
        { key: "required", label: "Required", type: "switch" },
      ]
    },
    defaults: { key: "phone" },
    tier: "Free"
  },

  number: {
    type: "number",
    group: "Core",
    label: "Number",
    icon: "Hash",
    capabilities: { validation: true, mapping: true, logic: true, prefill: true },
    answer: { type: "number" },
    configSchema: {
      required: ["key"],
      fields: [
        { key: "key", label: "Field key", type: "text" },
        { key: "label", label: "Label", type: "text" },
        { key: "required", label: "Required", type: "switch" },
        { key: "validations.min", label: "Min", type: "number" },
        { key: "validations.max", label: "Max", type: "number" },
      ]
    },
    defaults: { key: "number" },
    tier: "Free"
  },

  date: {
    type: "date",
    group: "Core",
    label: "Date",
    icon: "Calendar",
    capabilities: { validation: true, mapping: true, logic: true, prefill: true },
    answer: { type: "date" },
    configSchema: {
      required: ["key"],
      fields: [
        { key: "key", label: "Field key", type: "text" },
        { key: "label", label: "Label", type: "text" },
        { key: "required", label: "Required", type: "switch" },
      ]
    },
    defaults: { key: "date" },
    tier: "Free"
  },

  /* -------------------- CHOICES -------------------- */
  single_choice: {
    type: "single_choice",
    group: "Choices",
    label: "Single Choice",
    icon: "Radio",
    capabilities: { validation: true, mapping: true, logic: true, prefill: true, localization: true },
    answer: { type: "string" },
    configSchema: {
      required: ["key"],
      fields: [
        { key: "key", label: "Field key", type: "text" },
        { key: "label", label: "Question", type: "text" },
        { key: "required", label: "Required", type: "switch" },
      ]
    },
    defaults: { key: "choice", required: false },
    tier: "Free"
  },

  multi_select: {
    type: "multi_select",
    group: "Choices",
    label: "Multi Select",
    icon: "Checklist",
    capabilities: { validation: true, mapping: true, logic: true, prefill: true },
    answer: { type: "array" },
    configSchema: {
      required: ["key"],
      fields: [
        { key: "key", label: "Field key", type: "text" },
        { key: "label", label: "Question", type: "text" },
        { key: "required", label: "Required", type: "switch" },
      ]
    },
    defaults: { key: "tags" },
    tier: "Free"
  },

  rating: {
    type: "rating",
    group: "Core",
    label: "Rating",
    icon: "Star",
    capabilities: { validation: true, logic: true, mapping: true },
    answer: { type: "number" },
    configSchema: {
      required: ["key"],
      fields: [
        { key: "key", label: "Key", type: "text" },
        { key: "label", label: "Question", type: "text" },
        { key: "required", label: "Required", type: "switch" },
      ]
    },
    defaults: { key: "rating", localize: true },
    tier: "Free"
  },

  /* -------------------- CONTENT & MEDIA -------------------- */
  description: {
    type: "description",
    group: "Content",
    label: "Description",
    icon: "Paragraph",
    capabilities: { localization: true },
    answer: { type: "void" },
    configSchema: {
      fields: [
        { key: "helpText", label: "Content", type: "textarea" }
      ]
    },
    defaults: {},
    tier: "Free"
  },

  title: {
    type: "title",
    group: "Content",
    label: "Title",
    icon: "Heading",
    capabilities: { localization: true },
    answer: { type: "void" },
    configSchema: {
      fields: [
        { key: "label", label: "Title", type: "text" },
        { key: "helpText", label: "Subtitle", type: "text" }
      ]
    },
    defaults: {},
    tier: "Free"
  },

  /* -------------------- LAYOUT -------------------- */
  page_break: {
    type: "page_break",
    group: "Layout",
    label: "Page Break",
    icon: "Page",
    capabilities: {},
    answer: { type: "void" },
    configSchema: { fields: [] },
    defaults: {},
    tier: "Free"
  },

  /* -------------------- FILE UPLOAD -------------------- */
  file_upload: {
    type: "file_upload",
    group: "Core",
    label: "File Upload",
    icon: "Upload",
    capabilities: { validation: true, mapping: true, logic: true, fileUpload: true },
    answer: { type: "file" },
    configSchema: {
      required: ["key"],
      fields: [
        { key: "key", label: "Field key", type: "text" },
        { key: "label", label: "Label", type: "text" },
        { key: "required", label: "Required", type: "switch" },
      ]
    },
    defaults: { key: "attachment", localize: false },
    tier: "Pro"
  },

  signature: {
    type: "signature",
    group: "Core",
    label: "Signature",
    icon: "Signature",
    capabilities: { validation: true, mapping: true, logic: true },
    answer: { type: "object" },
    configSchema: {
      required: ["key"],
      fields: [
        { key: "key", label: "Field key", type: "text" },
        { key: "label", label: "Label", type: "text" },
        { key: "required", label: "Required", type: "switch" },
      ]
    },
    defaults: { key: "signature", required: true },
    tier: "Pro"
  },

  /* -------------------- IDENTITY -------------------- */
  full_name: {
    type: "full_name",
    group: "Identity",
    label: "Full Name",
    icon: "User",
    capabilities: { mapping: true, validation: true, prefill: true, localization: true },
    answer: { type: "object" },
    configSchema: {
      required: ["key"],
      fields: [
        { key: "key", label: "Key", type: "text" },
        { key: "label", label: "Label", type: "text" },
      ]
    },
    defaults: { key: "full_name" },
    tier: "Free"
  },

  address_global: {
    type: "address_global",
    group: "Identity",
    label: "Address",
    icon: "MapPin",
    capabilities: { mapping: true, validation: true, prefill: true },
    answer: { type: "object" },
    configSchema: {
      required: ["key"],
      fields: [
        { key: "key", label: "Key", type: "text" },
        { key: "label", label: "Label", type: "text" },
      ]
    },
    defaults: { key: "address" },
    tier: "Pro"
  },

  /* -------------------- RELOCATION -------------------- */
  document_checklist: {
    type: "document_checklist",
    group: "Relocation",
    label: "Document Checklist",
    icon: "ChecklistDoc",
    capabilities: { fileUpload: true, mapping: true, logic: true },
    answer: { type: "array" },
    configSchema: {
      required: ["key"],
      fields: [
        { key: "key", label: "Key", type: "text" },
        { key: "label", label: "Title", type: "text" },
      ]
    },
    defaults: { key: "docs_required" },
    tier: "Pro"
  },

  appointment_picker: {
    type: "appointment_picker",
    group: "Relocation",
    label: "Appointment Picker",
    icon: "CalendarCheck",
    capabilities: { mapping: true, logic: true },
    answer: { type: "object" },
    configSchema: {
      required: ["key"],
      fields: [
        { key: "key", label: "Key", type: "text" },
        { key: "label", label: "Label", type: "text" },
      ]
    },
    defaults: { key: "appointment" },
    tier: "Pro"
  },

  household_repeater: {
    type: "household_repeater",
    group: "Relocation",
    label: "Household & Dependents",
    icon: "Users",
    capabilities: { repeatable: true, mapping: true },
    answer: { type: "array" },
    configSchema: {
      required: ["key"],
      fields: [
        { key: "key", label: "Key", type: "text" },
        { key: "label", label: "Label", type: "text" },
      ]
    },
    defaults: { key: "household" },
    tier: "Pro"
  },

  city_preference: {
    type: "city_preference",
    group: "Relocation",
    label: "City Preference",
    icon: "City",
    capabilities: { logic: true, mapping: true },
    answer: { type: "object" },
    configSchema: {
      required: ["key"],
      fields: [
        { key: "key", label: "Key", type: "text" },
        { key: "label", label: "Question", type: "text" },
      ]
    },
    defaults: { key: "city_preference" },
    tier: "Pro"
  },

  consent_gdpr: {
    type: "consent_gdpr",
    group: "Relocation",
    label: "GDPR Consent",
    icon: "ShieldCheck",
    capabilities: { mapping: true, localization: true },
    answer: { type: "object" },
    configSchema: {
      required: ["key"],
      fields: [
        { key: "key", label: "Key", type: "text" },
        { key: "label", label: "Headline", type: "text" },
        { key: "required", label: "Required", type: "switch" },
      ]
    },
    defaults: { key: "consent" },
    tier: "Free"
  },
};

export const BLOCK_GROUPS = [
  "Core", "Choices", "Content", "Layout", "Media", "Logic", 
  "Identity", "Relocation", "Commerce", "Security", "Submission", "Power"
];

export function getBlocksByGroup(group) {
  return Object.values(BLOCKS).filter(b => b.group === group);
}

export function getBlockDefinition(type) {
  return BLOCKS[type];
}

